import { SeedFilter } from '@/hooks/useSeeds';
import { SeedType } from '@/sections/seeds/types';

const STORAGE_KEY_PREFIX = 'chariot-seed-filters';
const CURRENT_VERSION = 1;

interface StoredFilterData {
  version: number;
  filters: SeedFilter;
}

/**
 * Seed filter localStorage utilities with schema versioning
 *
 * Provides persistent storage for seed filters with version management
 * to support schema migrations across application updates.
 */
export const seedFilterStorage = {
  /**
   * Get filters from localStorage with version migration
   *
   * @param seedType - The type of seed to get filters for
   * @returns The stored filters, or null if no valid filters exist
   */
  get: (seedType: SeedType): SeedFilter | null => {
    try {
      const key = `${STORAGE_KEY_PREFIX}-${seedType}`;
      const stored = localStorage.getItem(key);

      if (!stored) return null;

      const parsed: StoredFilterData = JSON.parse(stored);

      // Version migration - future version detected
      if (parsed.version > CURRENT_VERSION) {
        console.warn(
          `Future schema version detected (v${parsed.version}), clearing storage`
        );
        localStorage.removeItem(key);
        return null;
      }

      // Version migration - old version detected
      if (parsed.version < CURRENT_VERSION) {
        const migrated = migrateFilters(parsed, CURRENT_VERSION);
        // Save migrated version
        seedFilterStorage.set(seedType, migrated);
        return migrated;
      }

      return parsed.filters;
    } catch (error) {
      console.error('Failed to parse stored seed filters', error);
      return null;
    }
  },

  /**
   * Save filters to localStorage with version
   *
   * @param seedType - The type of seed to save filters for
   * @param filters - The filter state to persist
   */
  set: (seedType: SeedType, filters: SeedFilter) => {
    const key = `${STORAGE_KEY_PREFIX}-${seedType}`;
    const data: StoredFilterData = {
      version: CURRENT_VERSION,
      filters,
    };
    localStorage.setItem(key, JSON.stringify(data));
  },

  /**
   * Clear filters from localStorage
   *
   * @param seedType - The type of seed to clear filters for
   */
  clear: (seedType: SeedType) => {
    const key = `${STORAGE_KEY_PREFIX}-${seedType}`;
    localStorage.removeItem(key);
  },

  /**
   * Clear all seed filters from localStorage
   */
  clearAll: () => {
    const types: SeedType[] = ['domain', 'ip', 'webapplication', 'preseed'];
    types.forEach(type => {
      const key = `${STORAGE_KEY_PREFIX}-${type}`;
      localStorage.removeItem(key);
    });
  },
};

/**
 * Migrate filter schema between versions
 *
 * @param old - The old filter data with version
 * @param _targetVersion - The target schema version (unused until migrations needed)
 * @returns The migrated filters
 */
const migrateFilters = (
  old: StoredFilterData,
  _targetVersion: number
): SeedFilter => {
  let filters = old.filters;

  // Add version-specific migrations here as schema evolves
  // Example:
  // if (old.version === 0 && _targetVersion >= 1) {
  //   filters = {
  //     ...filters,
  //     newField: defaultValue,
  //   };
  // }

  return filters;
};
